// GoogleDriveExplorer.jsx   –   yalnızca MUI + @mui/x-tree-view + @mui/icons-material
import React, { useEffect, useMemo } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import ImageIcon from '@mui/icons-material/Image';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { useTreeItem } from '@mui/x-tree-view/useTreeItem';
import { TreeItemIconContainer, TreeItemLabel } from '@mui/x-tree-view/TreeItem';
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon';
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider';
import { TreeItemDragAndDropOverlay } from '@mui/x-tree-view/TreeItemDragAndDropOverlay';
import { useTreeItemModel } from '@mui/x-tree-view/hooks';

import { useQuery } from '@tanstack/react-query';
import { getFilesGoogleDrive, getOrCreateFilesGoogleDrive } from 'services/api/general/file';

/* ───── 1. MIME → fileType → ikon ───── */
const mimeToFileType = (mime) => {
    if (!mime) return 'doc';
    if (mime === 'application/vnd.google-apps.folder') return 'folder';
    if (mime.includes('pdf')) return 'pdf';
    if (mime.includes('image')) return 'image';
    if (mime.includes('video')) return 'video';
    return 'doc';
};

const getIconFromFileType = (fileType) => {
    switch (fileType) {
        case 'image':
            return ImageIcon;
        case 'pdf':
            return PictureAsPdfIcon;
        case 'doc':
            return ArticleIcon;
        case 'video':
            return VideoCameraBackIcon;
        case 'folder':
            return FolderRoundedIcon;
        case 'pinned':
            return FolderOpenIcon;
        case 'trash':
            return DeleteIcon;
        default:
            return ArticleIcon;
    }
};

/* ───── 2. GitHub benzeri temiz stiller ───── */
const TreeItemRoot = styled('li')(({ theme }) => ({
    listStyle: 'none',
    margin: 0,
    padding: 0,
    outline: 0,
}));

const TreeItemContent = styled('div')(({ theme }) => ({
    padding: theme.spacing(0.25, 1),
    paddingLeft: `calc(${theme.spacing(1)} + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))`,
    width: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    borderRadius: theme.spacing(0.5),
    margin: theme.spacing(0.125, 0),
    minHeight: 28,
    fontSize: '14px',
    color: theme.palette.text.primary,
    transition: 'background-color 0.1s ease',

    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(177, 186, 196, 0.12)' : 'rgba(208, 215, 222, 0.32)',
    },

    [`&[data-focused], &[data-selected]`]: {
        backgroundColor: theme.palette.mode === 'dark' ? '#1f6feb' : '#0969da',
        color: '#ffffff',

        '& .MuiSvgIcon-root': {
            color: '#ffffff',
        },
    },
}));

const CustomCollapse = styled(Collapse)({
    padding: 0,
});

const AnimatedCollapse = animated(CustomCollapse);

function TransitionComponent(props) {
    const style = useSpring({
        to: {
            opacity: props.in ? 1 : 0,
            transform: `translate3d(0,${props.in ? 0 : 10}px,0)`,
        },
        config: { tension: 300, friction: 30 },
    });

    return <AnimatedCollapse style={style} {...props} />;
}

const TreeItemLabelText = styled(Typography)(({ theme }) => ({
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: 'inherit',
    userSelect: 'none',
}));

/* ───── 3. GitHub benzeri label bileşeni ───── */
function CustomLabel({ icon: Icon, expandable, link, children, ...other }) {
    return (
        <TreeItemLabel
            {...other}
            sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                minWidth: 0, // Allow text truncation
            }}
        >
            {Icon && (
                <Box
                    component={Icon}
                    sx={{
                        mr: 0.75,
                        fontSize: '16px',
                        color: 'inherit',
                        opacity: 0.7,
                        flexShrink: 0,
                    }}
                />
            )}
            <TreeItemLabelText variant="body2" noWrap sx={{ flex: 1 }}>
                {children}
            </TreeItemLabelText>
            {link && (
                <OpenInNewIcon
                    fontSize="small"
                    sx={{
                        ml: 0.5,
                        cursor: 'pointer',
                        opacity: 0.6,
                        fontSize: '14px',
                        '&:hover': {
                            opacity: 1,
                        },
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        window.open(link, '_blank');
                    }}
                />
            )}
        </TreeItemLabel>
    );
}

/* ───── 4. GitHub benzeri CustomTreeItem ───── */
const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {
    const { id, itemId, label, disabled, children, ...other } = props;
    const {
        getContextProviderProps,
        getRootProps,
        getContentProps,
        getIconContainerProps,
        getLabelProps,
        getGroupTransitionProps,
        getDragAndDropOverlayProps,
        status,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

    const item = useTreeItemModel(itemId);

    let icon;
    if (status.expandable) {
        icon = FolderRoundedIcon;
    } else if (item.fileType) {
        icon = getIconFromFileType(item.fileType);
    }

    return (
        <TreeItemProvider {...getContextProviderProps()}>
            <TreeItemRoot {...getRootProps(other)}>
                <TreeItemContent {...getContentProps()}>
                    <TreeItemIconContainer {...getIconContainerProps()}>
                        <TreeItemIcon status={status} />
                    </TreeItemIconContainer>
                    <CustomLabel
                        {...getLabelProps({
                            icon,
                            link: item.webViewLink,
                        })}
                    />
                    <TreeItemDragAndDropOverlay {...getDragAndDropOverlayProps()} />
                </TreeItemContent>
                {children && <TransitionComponent {...getGroupTransitionProps()} />}
            </TreeItemRoot>
        </TreeItemProvider>
    );
});

/* ───── 5. Drive listesini RichTreeView items formatına çevir ───── */
function driveToItems(list) {
    const map = {};
    const roots = [];

    list.forEach((f) => {
        const parts = (f.path || f.name).split('/');
        let key = '';
        let parentArr = roots;
        parts.forEach((p, i) => {
            key = key ? `${key}/${p}` : p;
            if (!map[key]) {
                const leaf = i === parts.length - 1;
                const node = { id: leaf ? f.id : `v::${key}`, label: p, children: [] };
                map[key] = node;
                parentArr.push(node);
            }
            parentArr = map[key].children;
        });
        Object.assign(map[key], {
            fileType: mimeToFileType(f.mimeType),
            webViewLink: f.webViewLink,
            createdAt: f.createdAt,
        });
    });

    return roots;
}

/* ───── 6. Ana bileşen ───── */
export default function GoogleDriveExplorer({
    folderId,
    refreshTrigger = 0,
    getOrCreate = false,
    getOrCreateFolderName = '',
    documents = [],
    topName = 'Google Drive',
}) {
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['drive', folderId],
        queryFn: () =>
            getOrCreate
                ? getOrCreateFilesGoogleDrive(folderId, getOrCreateFolderName, documents)
                : getFilesGoogleDrive(folderId),
        enabled: !!folderId,
        staleTime: 600_000,
    });

    useEffect(() => {
        refetch();
    }, [refreshTrigger, refetch]);

    const items = useMemo(() => driveToItems(data?.data?.result ?? []), [data]);

    if (isLoading) return <>Yükleniyor…</>;
    if (error) return <>Hata oluştu</>;
    if (!items.length) return <>Dosya bulunamadı</>;

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                backgroundColor: 'background.paper',
                overflow: 'hidden',
                boxShadow:
                    '0rem 0.625rem 0.9375rem -0.1875rem rgba(0, 0, 0, 0.1),0rem 0.25rem 0.375rem -0.125rem rgba(0, 0, 0, 0.05)',
            }}
        >
            {/* GitHub benzeri başlık */}
            <Box
                sx={{
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'grey.50',
                    ...(theme) =>
                        theme.palette.mode === 'dark' && {
                            backgroundColor: 'grey.900',
                        },
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily:
                            '-apple-system, BlinkMacSystemFont, "Segoe UI", "Noto Sans", Helvetica, Arial, sans-serif',
                        fontWeight: 600,
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: 'text.primary',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        transition: 'color 0.1s ease',
                        '&:hover': {
                            color: 'primary.main',
                        },
                    }}
                    onClick={() => {
                        if (data?.data?.mainFolderId) {
                            window.open(`https://drive.google.com/drive/folders/${data.data.mainFolderId}`, '_blank');
                        } else if (folderId) {
                            window.open(`https://drive.google.com/drive/folders/${folderId}`, '_blank');
                        }
                    }}
                >
                    <FolderRoundedIcon sx={{ fontSize: '20px', mr: 0.5 }} />
                    {topName}
                    <OpenInNewIcon sx={{ fontSize: '14px', opacity: 0.6, ml: 0.5 }} />
                </Typography>
            </Box>

            {/* Dosya ağacı */}
            <RichTreeView
                items={items}
                defaultExpandedItems={items.map((n) => n.id)}
                slots={{ item: CustomTreeItem }}
                itemChildrenIndentation={16}
                sx={{
                    height: 'fit-content',
                    flexGrow: 1,
                    maxWidth: 420,
                    overflowY: 'auto',
                    p: 1.5,
                    '& .MuiTreeItem-iconContainer': {
                        width: 16,
                        marginRight: 0.5,
                    },
                    '& .MuiTreeItem-content': {
                        padding: 0,
                    },
                }}
            />
        </Box>
    );
}
